import {useCallback} from "react";
import {css} from "@panda/css";

import {Button, type IButtonIconProps} from "@pg-design/button-module";
import {ChevronDownIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {KM_COMMON_TEST_IDS} from "@pg-mono/test-ids";

import {NAVIGATION_LABELS} from "../constants/navigation_labels";
import {navigationChevronClosedStyle, navigationChevronOpenStyle} from "../constants/navigation_shared_styles";
import {UserProfileMenuVariant} from "../types/UserProfileMenuVariant";
import {UserProfileAvatar} from "./UserProfileAvatar";

interface IProps {
    avatarSrc?: string | null;
    greeting: string;
    isOpen: boolean;
    onToggle: () => void;
    variant: UserProfileMenuVariant;
}

export const UserProfileMenuTrigger = (props: IProps) => {
    const {avatarSrc, greeting, isOpen, onToggle, variant} = props;
    const chevronClassName = isOpen ? navigationChevronOpenStyle : navigationChevronClosedStyle;

    // Keep a stable component type so <img> is not remounted (and re-fetched) on every render.
    const renderDesktopAvatar = useCallback(
        (iconProps: IButtonIconProps) => (
            <UserProfileAvatar
                className={iconProps.className}
                fill="var(--colors-secondary)"
                size="1.6"
                src={avatarSrc}
                wrapperColor="var(--colors-beige-subtle)"
                wrapperSize="5.6"
            />
        ),
        [avatarSrc]
    );

    const renderMobileAvatar = useCallback(
        (iconProps: IButtonIconProps) => (
            <UserProfileAvatar
                className={iconProps.className}
                fill="var(--colors-gray-800)"
                size="1.8"
                src={avatarSrc}
                wrapperColor="var(--colors-beige-subtle)"
                wrapperSize="3.2"
            />
        ),
        [avatarSrc]
    );

    if (variant === UserProfileMenuVariant.DESKTOP) {
        return (
            <Button
                aria-expanded={isOpen}
                aria-haspopup="menu"
                className={profileCardTriggerStyle}
                dataTestId={KM_COMMON_TEST_IDS.PROFILE_MENU_TRIGGER}
                iconLeft={renderDesktopAvatar}
                iconRight={() => (
                    <span className={profileCardChevronBoxStyle}>
                        <ChevronDownIcon className={chevronClassName} fill="var(--colors-gray-800)" size="1.6" />
                    </span>
                )}
                onClick={onToggle}
                type="button"
                variant="none_secondary"
            >
                <span className={profileCardTextStyle}>
                    <Text as="span" className={profileCardGreetingStyle} color="var(--colors-secondary)" variant="info_txt_1">
                        {greeting}
                    </Text>
                    <Text as="span" color="var(--colors-gray-700)" variant="info_txt_3">
                        {NAVIGATION_LABELS.PROFILE_GREETING.SETTINGS_SUBTITLE}
                    </Text>
                </span>
            </Button>
        );
    }

    return (
        <Button
            aria-expanded={isOpen}
            aria-haspopup="menu"
            aria-label={NAVIGATION_LABELS.PROFILE_GREETING.SETTINGS_SUBTITLE}
            className={profilePillTriggerStyle}
            dataTestId={KM_COMMON_TEST_IDS.PROFILE_MENU_TRIGGER}
            iconLeft={renderMobileAvatar}
            iconRight={() => (
                <span className={profilePillChevronBoxStyle}>
                    <ChevronDownIcon className={chevronClassName} fill="var(--colors-gray-800)" size="1" />
                </span>
            )}
            onClick={onToggle}
            type="button"
            variant="none_secondary"
        />
    );
};

const profilePillTriggerStyle = css({
    border: "1px solid var(--colors-gray-400)",
    borderRadius: "9.9rem",
    padding: "0.2rem",
    backgroundColor: "var(--colors-white)",
    height: "3.6rem",
    minHeight: "unset",
    color: "var(--colors-gray-800)",
    fill: "var(--colors-gray-800)",
    transition: "background-color var(--transition-duration) var(--transition-timingFunction)",
    _active: {
        backgroundColor: "var(--colors-background-50)"
    },
    _hover: {
        backgroundColor: "var(--colors-background-50)"
    }
});

const chevronBoxSharedStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0.2rem",
    boxShadow: "var(--shadow-1)",
    backgroundColor: "var(--colors-white)",
    flexShrink: 0
} as const;

const profilePillChevronBoxStyle = css({
    ...chevronBoxSharedStyle,
    width: "1rem",
    height: "1rem",
    mx: 1
});

const profileCardTriggerStyle = css({
    display: "flex",
    alignItems: "center",
    width: "100%",
    justifyContent: "flex-start",
    border: "1px solid var(--colors-gray-400)",
    borderRadius: "9.9rem",
    height: "6rem",
    p: "0.2rem",
    whiteSpace: "normal",
    textTransform: "none",
    "& > span": {
        width: "100%",
        justifyContent: "flex-start",
        gap: 1
    },
    // ButtonText wrapper — grows so chevron stays on the right
    "& > span > *:nth-child(2)": {
        flex: 1,
        minWidth: 0,
        textAlign: "left"
    },
    // iconLeft already gets margin-right from Button; gap covers spacing — zero it
    "& > span > *:first-child": {
        marginRight: 0
    },
    transition: "background-color var(--transition-duration) var(--transition-timingFunction)",
    _active: {
        backgroundColor: "var(--colors-background-50)"
    },
    _hover: {
        backgroundColor: "var(--colors-background-50)"
    }
});

const profileCardChevronBoxStyle = css({
    ...chevronBoxSharedStyle,
    width: "1.6rem",
    height: "1.6rem",
    me: 1
});

const profileCardTextStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
    textAlign: "left",
    gap: 0.5,
    whiteSpace: "normal",
    textTransform: "none"
});

const profileCardGreetingStyle = css({
    wordBreak: "break-all",
    width: "100%",
    lineClamp: 2
});
import {useCallback} from "react";
import {css} from "@panda/css";

import {Button, type IButtonIconProps} from "@pg-design/button-module";
import {ChevronDownIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {KM_COMMON_TEST_IDS} from "@pg-mono/test-ids";

import {NAVIGATION_LABELS} from "../constants/navigation_labels";
import {navigationChevronClosedStyle, navigationChevronOpenStyle} from "../constants/navigation_shared_styles";
import {UserProfileMenuVariant} from "../types/UserProfileMenuVariant";
import {UserProfileAvatar} from "./UserProfileAvatar";

interface IProps {
    avatarSrc?: string | null;
    greeting: string;
    isOpen: boolean;
    onToggle: () => void;
    variant: UserProfileMenuVariant;
}

export const UserProfileMenuTrigger = (props: IProps) => {
    const {avatarSrc, greeting, isOpen, onToggle, variant} = props;
    const chevronClassName = isOpen ? navigationChevronOpenStyle : navigationChevronClosedStyle;

    // Keep a stable component type so <img> is not remounted (and re-fetched) on every render.
    const renderDesktopAvatar = useCallback(
        (iconProps: IButtonIconProps) => (
            <UserProfileAvatar
                className={iconProps.className}
                fill="var(--colors-secondary)"
                size="1.6"
                src={avatarSrc}
                wrapperColor="var(--colors-beige-subtle)"
                wrapperSize="5.6"
            />
        ),
        [avatarSrc]
    );

    const renderMobileAvatar = useCallback(
        (iconProps: IButtonIconProps) => (
            <UserProfileAvatar
                className={iconProps.className}
                fill="var(--colors-gray-800)"
                size="1.8"
                src={avatarSrc}
                wrapperColor="var(--colors-beige-subtle)"
                wrapperSize="3.2"
            />
        ),
        [avatarSrc]
    );

    if (variant === UserProfileMenuVariant.DESKTOP) {
        return (
            <Button
                aria-expanded={isOpen}
                aria-haspopup="menu"
                className={profileCardTriggerStyle}
                dataTestId={KM_COMMON_TEST_IDS.PROFILE_MENU_TRIGGER}
                iconLeft={renderDesktopAvatar}
                iconRight={() => (
                    <span className={profileCardChevronBoxStyle}>
                        <ChevronDownIcon className={chevronClassName} fill="var(--colors-gray-800)" size="1.6" />
                    </span>
                )}
                onClick={onToggle}
                type="button"
                variant="none_secondary"
            >
                <span className={profileCardTextStyle}>
                    <Text as="span" className={profileCardGreetingStyle} color="var(--colors-secondary)" variant="info_txt_1">
                        {greeting}
                    </Text>
                    <Text as="span" color="var(--colors-gray-700)" variant="info_txt_3">
                        {NAVIGATION_LABELS.PROFILE_GREETING.SETTINGS_SUBTITLE}
                    </Text>
                </span>
            </Button>
        );
    }

    return (
        <Button
            aria-expanded={isOpen}
            aria-haspopup="menu"
            aria-label={NAVIGATION_LABELS.PROFILE_GREETING.SETTINGS_SUBTITLE}
            className={profilePillTriggerStyle}
            dataTestId={KM_COMMON_TEST_IDS.PROFILE_MENU_TRIGGER}
            iconLeft={renderMobileAvatar}
            iconRight={() => (
                <span className={profilePillChevronBoxStyle}>
                    <ChevronDownIcon className={chevronClassName} fill="var(--colors-gray-800)" size="1" />
                </span>
            )}
            onClick={onToggle}
            type="button"
            variant="none_secondary"
        />
    );
};

const profilePillTriggerStyle = css({
    border: "1px solid var(--colors-gray-400)",
    borderRadius: "9.9rem",
    padding: "0.2rem",
    backgroundColor: "var(--colors-white)",
    height: "3.6rem",
    minHeight: "unset",
    color: "var(--colors-gray-800)",
    fill: "var(--colors-gray-800)",
    transition: "background-color var(--transition-duration) var(--transition-timingFunction)",
    _active: {
        backgroundColor: "var(--colors-background-50)"
    },
    _hover: {
        backgroundColor: "var(--colors-background-50)"
    }
});

const chevronBoxSharedStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "0.2rem",
    boxShadow: "var(--shadow-1)",
    backgroundColor: "var(--colors-white)",
    flexShrink: 0
} as const;

const profilePillChevronBoxStyle = css({
    ...chevronBoxSharedStyle,
    width: "1rem",
    height: "1rem",
    mx: 1
});

const profileCardTriggerStyle = css({
    display: "flex",
    alignItems: "center",
    width: "100%",
    justifyContent: "flex-start",
    border: "1px solid var(--colors-gray-400)",
    borderRadius: "9.9rem",
    height: "6rem",
    p: "0.2rem",
    whiteSpace: "normal",
    textTransform: "none",
    "& > span": {
        width: "100%",
        justifyContent: "flex-start",
        gap: 1
    },
    // ButtonText wrapper — grows so chevron stays on the right
    "& > span > *:nth-child(2)": {
        flex: 1,
        minWidth: 0,
        textAlign: "left"
    },
    // iconLeft already gets margin-right from Button; gap covers spacing — zero it
    "& > span > *:first-child": {
        marginRight: 0
    },
    transition: "background-color var(--transition-duration) var(--transition-timingFunction)",
    _active: {
        backgroundColor: "var(--colors-background-50)"
    },
    _hover: {
        backgroundColor: "var(--colors-background-50)"
    }
});

const profileCardChevronBoxStyle = css({
    ...chevronBoxSharedStyle,
    width: "1.6rem",
    height: "1.6rem",
    me: 1
});

const profileCardTextStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    width: "100%",
    textAlign: "left",
    gap: 0.5,
    whiteSpace: "normal",
    textTransform: "none"
});

const profileCardGreetingStyle = css({
    wordBreak: "break-all",
    width: "100%",
    lineClamp: 2
});
